import { DocumentationLegacyPageBlockColumnItem } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockColumnItem";
import { DocumentationLegacyPageBlockTabItem } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockTabItem";
import { DocumentationLegacyPageBlockTableRow } from "../../model/documentation/blocks/SDKDocumentationLegacyPageBlockTableRow";
import { DocumentationLegacyCalloutType } from "../../model/enums/SDKDocumentationLegacyCalloutType";
import { DocumentationLegacyHeadingType } from "../../model/enums/SDKDocumentationLegacyHeadingType";
import { DocumentationLegacyPageBlockType } from "../../model/enums/SDKDocumentationLegacyPageBlockType";
import { MarkdownTransformType } from "./SDKToolsMarkdownTransform";
import { MarkdownTransformUtil } from "./SDKToolsMarkdownTransformUtil";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Definitions
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Search Instance
/** Markdown block transformer */
export class MarkdownTransformBlock {
    // --- Conversion
    constructor(type, instance, designSystemId, workspaceId, versionId) {
        this.newlineSeparator = "  \n";
        this.transformType = type;
        this.instance = instance;
        this.designSystemId = designSystemId;
        this.versionId = versionId;
        this.workspaceId = workspaceId;
        if (type === MarkdownTransformType.github) {
            console.log("Note: GitHub mode of markdown is currently in development and is not yet fully supported");
        }
        this.utilTransformer = new MarkdownTransformUtil();
    }
    // --- Conversion
    /** Converts a single block - depending on the type - to a markdown definition of specific type */
    async convertBlockToMarkdown(block, pageContext) {
        switch (block.type) {
            // Following blocks are automatically transformed
            case DocumentationLegacyPageBlockType.callout:
                return this.convertCalloutBlock(block);
            case DocumentationLegacyPageBlockType.code:
                return this.convertCodeBlock(block);
            case DocumentationLegacyPageBlockType.componentAssets:
                return this.convertComponentAssetBlock(block);
            case DocumentationLegacyPageBlockType.custom:
                return this.convertCustomBlock();
            case DocumentationLegacyPageBlockType.divider:
                return this.convertDividerBlock();
            case DocumentationLegacyPageBlockType.figmaEmbed:
                return this.convertFigmaEmbedBlock(block);
            case DocumentationLegacyPageBlockType.figmaFrames:
                return this.convertFigmaFramesBlock(block);
            case DocumentationLegacyPageBlockType.genericEmbed:
                return this.convertGenericEmbedBlock(block);
            case DocumentationLegacyPageBlockType.heading:
                return this.convertHeadingBlock(block);
            case DocumentationLegacyPageBlockType.image:
                return this.convertImageBlock(block);
            case DocumentationLegacyPageBlockType.link:
                return this.convertLinkBlock(block);
            case DocumentationLegacyPageBlockType.orderedList:
                return this.convertOrderedListBlock(block);
            case DocumentationLegacyPageBlockType.quote:
                return this.convertQuoteBlock(block);
            case DocumentationLegacyPageBlockType.renderCode:
                return this.convertLiveCodeBlock(block);
            case DocumentationLegacyPageBlockType.shortcuts:
                return this.convertShortcutsBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.storybookEmbed:
                return this.convertStorybookEmbedBlock(block);
            case DocumentationLegacyPageBlockType.text:
                return this.convertTextBlock(block);
            case DocumentationLegacyPageBlockType.token:
                return this.convertTokenBlock(block);
            case DocumentationLegacyPageBlockType.tokenGroup:
                return this.convertTokenGroupBlock(block);
            case DocumentationLegacyPageBlockType.tokenList:
                return this.convertTokenListBlock(block);
            case DocumentationLegacyPageBlockType.unorderedList:
                return this.convertUnorderedListBlock(block);
            case DocumentationLegacyPageBlockType.youtubeEmbed:
                return this.convertYoutubeEmbedBlock(block);
            // Following blocks are special because their transformation is invoked manually (containers)
            case DocumentationLegacyPageBlockType.column:
                return this.convertColumnBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.table:
                return this.convertTableBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tabs:
                return this.convertTabsBlock(block, pageContext);
            // Following blocks are special because their transformation is invoked manually (contained items)
            case DocumentationLegacyPageBlockType.tabItem:
                return this.convertTabItemBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.columnItem:
                return this.convertColumnItemBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tableCell:
                return this.convertTableCellBlock(block, pageContext);
            case DocumentationLegacyPageBlockType.tableRow:
                return this.convertTableRowBlock();
            default:
                throw new Error("Unknown block type");
        }
    }
    // -- Plain blocks
    convertHeadingBlock(block) {
        const heading = block;
        const text = this.utilTransformer.convertTextBlockToMarkdown(heading);
        switch (heading.headingType) {
            case DocumentationLegacyHeadingType.h1:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `# ${text}`;
            case DocumentationLegacyHeadingType.h2:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `## ${text}`;
            case DocumentationLegacyHeadingType.h3:
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                return `### ${text}`;
            default:
                throw new Error("Unknown heading type");
        }
    }
    convertCalloutBlock(block) {
        const text = this.utilTransformer.convertTextBlockToMarkdown(block);
        let calloutType;
        switch (block.calloutType) {
            case DocumentationLegacyCalloutType.info:
                calloutType = "Some extra info:";
                break;
            case DocumentationLegacyCalloutType.warning:
                calloutType = "Be warned:";
                break;
            case DocumentationLegacyCalloutType.success:
                calloutType = "Yay:";
                break;
            case DocumentationLegacyCalloutType.error:
                calloutType = "Please note:";
                break;
            default:
                throw new Error("Unknown callout type");
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `> ${calloutType}${this.newlineSeparator}> ${text}`;
    }
    convertQuoteBlock(block) {
        const text = this.utilTransformer.convertTextBlockToMarkdown(block);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `> ${text}`;
    }
    convertDividerBlock() {
        return "---";
    }
    convertImageBlock(block) {
        if (block.url) {
            return `![Img](${block.url})`;
        }
        return null;
    }
    convertOrderedListBlock(block) {
        const text = this.utilTransformer.convertTextBlockToMarkdown(block);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `1. ${text}`;
    }
    convertUnorderedListBlock(block) {
        const text = this.utilTransformer.convertTextBlockToMarkdown(block);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `- ${text}`;
    }
    convertLiveCodeBlock(block) {
        const codeLanguageDefinition = "javascript";
        return `\`\`\`${codeLanguageDefinition}${this.newlineSeparator}${block.code}${this.newlineSeparator}\`\`\``;
    }
    convertCodeBlock(block) {
        const codeLanguageDefinition = block.codeLanguage
            ? block.codeLanguage.toLowerCase()
            : "";
        return `\`\`\`${codeLanguageDefinition}${this.newlineSeparator}${block.text.asPlainText()}${this.newlineSeparator}\`\`\``;
    }
    convertTextBlock(block) {
        return this.utilTransformer.convertTextBlockToMarkdown(block);
    }
    convertGenericEmbedBlock(block) {
        return this.convertURLBlock(block, "Open link");
    }
    convertStorybookEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Storybook Canvas");
    }
    convertLinkBlock(block) {
        return this.convertURLBlock(block, "Open link");
    }
    convertYoutubeEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Youtube Video");
    }
    convertFigmaEmbedBlock(block) {
        return this.convertURLBlock(block, "Open Figma Prototype / File");
    }
    // -- Token blocks
    async convertTokenBlock(block) {
        if (!block.tokenId) {
            return null;
        }
        // Fetch token
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        tokens = tokens.filter((t) => t.id === block.tokenId || t.idInVersion === block.tokenId);
        const firstToken = tokens[0];
        if (tokens.length === 0 || !firstToken) {
            return null;
        }
        // Convert token
        return `${this.newlineSeparator}${this.utilTransformer.convertTokenToMarkdown(firstToken)}${this.newlineSeparator}`;
    }
    async convertTokenListBlock(block) {
        if (!block.tokenIds) {
            return null;
        }
        // Fetch tokens
        let tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        tokens = tokens.filter((t) => block.tokenIds.includes(t.id) || block.tokenIds.includes(t.idInVersion));
        if (tokens.length === 0) {
            return null;
        }
        // Convert token
        return `${this.newlineSeparator}${tokens
            .map((t) => this.utilTransformer.convertTokenToMarkdown(t))
            .join(this.newlineSeparator)}${this.newlineSeparator}`;
    }
    async convertTokenGroupBlock(block) {
        if (!block.groupId) {
            return null;
        }
        // Fetch tokens
        const tokens = await this.instance.tokens.getTokens({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        let groups = await this.instance.tokens.getTokenGroups({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        groups = groups.filter((g) => g.id === block.groupId || g.idInVersion === block.groupId);
        const firstGroup = groups[0];
        if (groups.length !== 1 || !firstGroup) {
            return null;
        }
        // Show either single group or all nested groups
        let groupsToShow = [firstGroup];
        if (block.showNestedGroups) {
            groupsToShow = this.flattenedGroupsFromRoot(firstGroup, groups);
        }
        // Convert group and all its tokens
        const segments = [];
        for (const group of groupsToShow) {
            const tokensToShow = tokens.filter((t) => group.tokenIds.includes(t.id) ||
                group.tokenIds.includes(t.idInVersion));
            if (tokensToShow.length > 0) {
                const segment = `${this.newlineSeparator}${`**Token Group ${[
                    ...group.path,
                    group.name,
                ].join(" / ")}**:  `}${this.newlineSeparator}${tokensToShow
                    .map((t) => this.utilTransformer.convertTokenToMarkdown(t))
                    .join(this.newlineSeparator)}${this.newlineSeparator}`;
                segments.push(segment);
            }
        }
        // Convert to single string
        return segments.join(this.newlineSeparator);
    }
    flattenedGroupsFromRoot(root, allGroups) {
        let groups = [root];
        for (const groupId of root.subgroupIds) {
            const group = allGroups.find((g) => g.id === groupId);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            groups.push(group);
            // @ts-expect-error TS(2345): Argument of type 'TokenGroup | undefined' is not a... Remove this comment to see the full error message
            groups = groups.concat(this.flattenedGroupsFromRoot(group, allGroups));
        }
        return groups;
    }
    async convertComponentAssetBlock(block) {
        const assets = await this.instance.assets.getAssets({
            designSystemId: this.designSystemId,
            versionId: this.versionId,
        });
        const convertedAssets = [];
        for (const asset of block.assets) {
            const assetObject = assets.filter((a) => a.id === asset.assetId)[0];
            if (assetObject && assetObject.thumbnailUrl) {
                convertedAssets.push(`![${assetObject.name}](${assetObject.thumbnailUrl})${this.newlineSeparator}${asset.title ? asset.title : assetObject.name}${asset.description
                    ? `, ${asset.description}${this.newlineSeparator}`
                    : ""}`);
            }
        }
        return (this.newlineSeparator +
            convertedAssets.join(this.newlineSeparator) +
            this.newlineSeparator);
    }
    convertFigmaFramesBlock(block) {
        var _a;
        const convertedFrames = [];
        for (const frame of block.frames) {
            if (frame.previewUrl) {
                convertedFrames.push(`![${(_a = frame.title) !== null && _a !== void 0 ? _a : "Image"}](${frame.previewUrl})${this.newlineSeparator}${frame.title ? frame.title : ""}${frame.description
                    ? `, ${frame.description}${this.newlineSeparator}`
                    : ""}`);
            }
        }
        return (this.newlineSeparator +
            convertedFrames.join(this.newlineSeparator) +
            this.newlineSeparator);
    }
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async convertShortcutsBlock(
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    block, 
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    pageContext) {
        /**
        let url = "#"
        const convertedShortcut: Array<string> = []
    
        const pages = await this.instance.documentation.getDocumentationPages({
          workspaceId: this.workspaceId,
          designSystemId: this.designSystemId,
          versionId: this.versionId,
        })
    
        for (const shortcut of block.shortcuts) {
          if (shortcut.externalUrl) {
            url = shortcut.externalUrl
          } else if (shortcut.internalId) {
            const destinationPage = pages.filter(
              (p) =>
                p.id === shortcut.internalId ||
                p.persistentId === shortcut.internalId
            )[0]
    
            if (destinationPage) {
              url = this.relativePathToPage(destinationPage, pageContext)
            }
          }
    
          if (shortcut.previewUrl) {
            // Generate shortcut with image preview
            const previewUrl = shortcut.previewUrl
            const title = shortcut.title ?? undefined
            const description = shortcut.description ?? undefined
    
            convertedShortcut.push(
              `[![${title}](${previewUrl})](${url})${this.newlineSeparator}${
                title || ""
              }${
                description
                  ? `, ${description}${this.newlineSeparator}`
                  : this.newlineSeparator
              }`
            )
          } else {
            // Generate shortcut without image preview
            const title = shortcut.title ?? undefined
            const description = shortcut.description ?? undefined
    
            convertedShortcut.push(
              `[${title}](${url})${
                description
                  ? `, ${description}${this.newlineSeparator}`
                  : this.newlineSeparator
              }`
            )
          }
        }
    
        return (
          this.newlineSeparator +
          convertedShortcut.join(this.newlineSeparator) +
          this.newlineSeparator
        )
        */
        return "";
    }
    relativePathToPage(page, currentPage) {
        // Compute level of depth
        let { parent } = currentPage;
        let depth = 0;
        while (parent) {
            depth += 1;
            // @ts-expect-error TS(2322): Type 'DocumentationLegacyGroup | null' is not assi... Remove this comment to see the full error message
            parent = parent.parent;
        }
        // Compute path
        const prefix = depth === 1 ? "./" : "../".repeat(depth - 1);
        let pageUrl = page.relativePageUrl();
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        if (pageUrl.endsWith("html")) {
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            pageUrl = `${pageUrl.substring(0, pageUrl.length - 4)}md`;
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        return prefix + pageUrl;
    }
    // -- Containers: Table
    async convertTableBlock(block, pageContext) {
        // No empty tables
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        // Generate header row
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        const firstRow = block.children[0];
        const rowContent = [];
        const separatorContent = [];
        let count = 1;
        // TODO:fix-sdk-eslint
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        for (const child of firstRow.children) {
            rowContent.push(`Column ${count}`);
            separatorContent.push(`---`);
            count += 1;
        }
        const tableRows = [
            `| ${rowContent.join(" | ")} |`,
            `| ${separatorContent.join(" | ")} |`,
        ];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockTableRow) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const childContent = await Promise.all(
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                child.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
                const rowDefinition = `| ${childContent.join(" | ")} |`;
                tableRows.push(rowDefinition);
            }
        }
        return (this.newlineSeparator +
            tableRows.join(this.newlineSeparator) +
            this.newlineSeparator);
    }
    convertTableRowBlock() {
        // Not used, rows are used when generating table directly in the table
        return null;
    }
    async convertTableCellBlock(block, pageContext) {
        const cellContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
        return cellContent.join("<br>"); // Multiline cell needs non-regular line breaks
    }
    // -- Containers: Tabs
    async convertTabsBlock(block, pageContext) {
        // No empty tabs
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        const tabContent = [];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockTabItem) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const result = await this.convertTabItemBlock(child, pageContext);
                // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                tabContent.push(result);
            }
        }
        return tabContent.join(this.newlineSeparator) + this.newlineSeparator;
    }
    async convertTabItemBlock(block, pageContext) {
        const childContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
        const tabDefinition = `${this.newlineSeparator}**${block.caption}**${this.newlineSeparator}${childContent.join(this.newlineSeparator)}`;
        return tabDefinition;
    }
    // -- Containers: Columns
    async convertColumnBlock(block, pageContext) {
        // No empty columns
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        if (block.children.length === 0) {
            return null;
        }
        const columnContent = [];
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const child of block.children) {
            if (child instanceof DocumentationLegacyPageBlockColumnItem) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-await-in-loop
                const result = await this.convertColumnItemBlock(child, pageContext);
                // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                columnContent.push(result);
            }
        }
        return columnContent.join(this.newlineSeparator) + this.newlineSeparator;
    }
    async convertColumnItemBlock(block, pageContext) {
        const childContent = await Promise.all(
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        block.children.map((c) => this.convertBlockToMarkdown(c, pageContext)));
        const tabDefinition = this.newlineSeparator + childContent.join(this.newlineSeparator);
        return tabDefinition;
    }
    // -- Unsupported
    convertCustomBlock() {
        // Custom blocks will not be transformed for now, as we can't realistically
        // know what they are supposed to do and right now we also can't invoke them
        return null;
        // TODO: Custom component blocks should still be converted, probably
    }
    // -- Conveniences
    convertURLBlock(block, userAction) {
        // Will generate:
        // [Action prompt, ie. "Open Figma File"](url)
        // ^ caption_if_provided
        if (block.url) {
            const caption = block.caption
                ? `^ ${block.caption}${this.newlineSeparator}`
                : "";
            return `${this.newlineSeparator}[${userAction}](${block.url})${this.newlineSeparator}${caption}`;
        }
        return null;
    }
}
